<template>
  <div class="login-teaser">
    <p class="login-teaser__intro">{{ $t('loginMenu.intro') }}</p>
    <div class="login-teaser__chips login-teaser__chips--targets">
      <span
        v-for="id in targets"
        :key="id"
        class="login-teaser__chip"
      >{{ $t('createBrief.templates.' + id + '.title') }}</span>
    </div>

    <div class="seo-score-group">
      <div class="seo-score-group__chart">
        <SeoScoreProgressBar
          :green="greenPct"
          :yellow="yellowPct"
        >
          <div class="d-flex flex-column align-center">
            <div class="text-h4">{{ score }}%</div>
            <div class="mt-1 text-caption">{{ $t('seoScore.score') }}</div>
          </div>
        </SeoScoreProgressBar>
        <div class="seo-score-legend">
          <button
            v-for="item in legend"
            :key="item.status"
            type="button"
            class="seo-score-count"
            :class="[
              'seo-score-count--' + item.status.toLowerCase(),
              { 'seo-score-count--active': statusFilter === item.status }
            ]"
            @click="selectStatus(item.status)"
          >
            <strong>{{ item.count }}</strong>
            <span>{{ item.label }}</span>
          </button>
        </div>
      </div>
      <p class="login-teaser__hint">{{ $t('loginMenu.scoreHint') }}</p>
      <div class="login-teaser__factors">
        <button
          v-for="name in visibleMetrics"
          :key="name"
          type="button"
          class="login-teaser__factor"
          :class="{ 'login-teaser__factor--open': openMetric === name }"
          @click="toggleMetric(name)"
        >
          <span class="login-teaser__factor-row">
            <SeoScoreIcon :status="demoStatus(name)" />
            <span class="login-teaser__factor-label">{{ $t('seoScore.' + name + '.label') }}</span>
          </span>
          <span
            v-if="openMetric === name"
            class="login-teaser__factor-tip"
          >
            {{ factorTip(name) }}
            <template v-if="demoStatus(name) !== 'GREEN'">
              {{ $t('loginMenu.factorHint') }}
            </template>
          </span>
        </button>
      </div>
    </div>

    <div class="login-teaser__lucy">
      <span class="login-teaser__lucy-mark">L</span>
      <div>
        <strong>{{ $t('loginMenu.lucyTitle') }}</strong>
        <p>{{ $t('loginMenu.lucyBody') }}</p>
      </div>
    </div>

    <div class="login-teaser__steps-label">{{ $t('loginMenu.stepsTitle') }}</div>
    <div
      v-for="step in steps"
      :key="step.id"
      class="login-teaser__step login-teaser__step--open"
    >
      <span class="login-teaser__step-head">
        <v-icon small class="mr-2" color="primary">{{ step.icon }}</v-icon>
        <span>{{ $t('loginMenu.' + step.id + 'Title') }}</span>
      </span>
      <span class="login-teaser__step-body">{{ $t('loginMenu.' + step.id + 'Body') }}</span>
      <span
        v-if="step.id === 'keywords'"
        class="login-teaser__chips"
      >
        <span
          v-for="chip in sampleKeywords"
          :key="chip"
          class="login-teaser__chip"
        >{{ chip }}</span>
      </span>
    </div>

    <v-btn
      block
      depressed
      color="primary"
      class="text-none mt-4 login-teaser__cta"
      @click="focusLogin"
    >
      {{ $t('loginMenu.cta') }}
    </v-btn>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import SeoScoreProgressBar from '@/components/SeoScoreProgressBar.vue';
    import SeoScoreIcon from '@/components/seoscore/SeoScoreIcon.vue';
    import {SeoScoreMetricName, seoScoreMetricNames, SeoScoreStatus} from '@/model/SeoScore';
    import {GEO_TEMPLATE_IDS} from '@/config/WritingBrief';

    const demoRed: SeoScoreMetricName[] = [
        'aiVisibility', 'eatAuthor', 'faqPresent', 'schemaMarkup', 'imgAltIncludesKeyword', 'outboundLinks'
    ];
    const demoYellow: SeoScoreMetricName[] = [
        'eatDate', 'eatBio', 'keywordInH2', 'relatedKeywords', 'keywordDensity',
        'descriptionLength', 'paragraphLength', 'titleLength'
    ];

    export default Vue.extend({
        name: 'LoginMenuTeaser',
        components: {SeoScoreProgressBar, SeoScoreIcon},
        data: () => ({
            statusFilter: 'RED' as SeoScoreStatus,
            openMetric: 'aiVisibility' as SeoScoreMetricName | null
        }),
        computed: {
            redCount(): number {
                return demoRed.length;
            },
            yellowCount(): number {
                return demoYellow.length;
            },
            greenCount(): number {
                return seoScoreMetricNames.length - demoRed.length - demoYellow.length;
            },
            score(): number {
                return Math.round(((this.greenCount + this.yellowCount / 2) / seoScoreMetricNames.length) * 100);
            },
            greenPct(): number {
                return this.greenCount / seoScoreMetricNames.length * 100;
            },
            yellowPct(): number {
                return (this.greenCount + this.yellowCount) / seoScoreMetricNames.length * 100;
            },
            legend(): Array<{ status: SeoScoreStatus; count: number; label: string }> {
                return [
                    {status: 'RED', count: this.redCount, label: String(this.$t('seoScore.legendRed'))},
                    {status: 'YELLOW', count: this.yellowCount, label: String(this.$t('seoScore.legendYellow'))},
                    {status: 'GREEN', count: this.greenCount, label: String(this.$t('seoScore.legendGreen'))},
                ];
            },
            visibleMetrics(): SeoScoreMetricName[] {
                return seoScoreMetricNames.filter((name) => this.demoStatus(name) === this.statusFilter);
            },
            steps(): Array<{ id: string; icon: string }> {
                return [
                    {id: 'brief', icon: 'edit_note'},
                    {id: 'keywords', icon: 'manage_search'},
                    {id: 'writing', icon: 'article'},
                ];
            },
            sampleKeywords(): string[] {
                return [
                    String(this.$t('loginMenu.samples.1')),
                    String(this.$t('loginMenu.samples.2')),
                    String(this.$t('loginMenu.samples.3')),
                ];
            },
            targets(): string[] {
                return GEO_TEMPLATE_IDS.filter((id) => id !== 'universal');
            }
        },
        methods: {
            demoStatus(name: SeoScoreMetricName): SeoScoreStatus {
                if (demoRed.indexOf(name) !== -1) {
                    return 'RED';
                }
                if (demoYellow.indexOf(name) !== -1) {
                    return 'YELLOW';
                }
                return 'GREEN';
            },
            selectStatus(status: SeoScoreStatus): void {
                this.statusFilter = status;
                const first = seoScoreMetricNames.find((name) => this.demoStatus(name) === status) || null;
                this.openMetric = first;
            },
            toggleMetric(name: SeoScoreMetricName): void {
                this.openMetric = this.openMetric === name ? null : name;
            },
            factorTip(name: SeoScoreMetricName): string {
                if (this.demoStatus(name) === 'GREEN') {
                    return String(this.$t('lucy.done', [this.$t('seoScore.' + name + '.label')]));
                }
                if (name === 'aiVisibility') {
                    return String(this.$t('lucy.coach.aiVisibility.body', [
                        this.$t('seoScore.faqPresent.label')
                    ]));
                }
                if (this.$te('lucy.coach.' + name)) {
                    return String(this.$t('lucy.coach.' + name));
                }
                if (this.$te('seoScore.detail.tips.' + name)) {
                    return String(this.$t('seoScore.detail.tips.' + name));
                }
                return String(this.$t('seoScore.' + name + '.description'));
            },
            focusLogin(): void {
                const field = document.getElementById('seowritee-login-email');
                if (field) {
                    field.scrollIntoView({behavior: 'smooth', block: 'center'});
                    field.focus();
                }
            }
        }
    });
</script>

<style scoped lang="sass">
.login-teaser
    padding: 12px 12px 20px

.login-teaser__intro
    font-size: 13px
    font-weight: 700
    line-height: 1.4
    margin: 0 4px 10px

.seo-score-group
    width: 100%
    padding: 12px 8px 10px
    border-radius: 16px
    background: rgba(8, 135, 220, 0.1)
    box-shadow: inset 0 0 0 1px rgba(8, 135, 220, 0.22)

.theme--dark .seo-score-group
    background: rgba(8, 135, 220, 0.18)

.seo-score-legend
    display: grid
    grid-template-columns: 1fr 1fr 1fr
    gap: 6px
    margin: 10px 2px 8px

.seo-score-count
    display: flex
    flex-direction: column
    align-items: center
    justify-content: center
    min-height: 44px
    border: 0
    border-radius: 10px
    cursor: pointer
    padding: 6px 4px
    line-height: 1.15
    strong
        font-size: 16px
        font-weight: 800
    span
        font-size: 9px
        font-weight: 700
        letter-spacing: 0.02em
        text-transform: uppercase
        margin-top: 2px

.seo-score-count--red
    background: var(--v-error-base)
    color: #fff

.seo-score-count--yellow
    background: var(--v-warning-base)
    color: #fff

.seo-score-count--green
    background: var(--v-accent-base)
    color: #fff

.seo-score-count--active
    box-shadow: 0 0 0 2px #0887dc, 0 0 0 4px rgba(8, 135, 220, 0.25)

.login-teaser__hint
    font-size: 11px
    line-height: 1.4
    opacity: 0.75
    margin: 0 4px 8px

.login-teaser__factors
    display: flex
    flex-direction: column
    gap: 2px

.login-teaser__factor
    display: block
    width: 100%
    text-align: left
    border: 0
    background: transparent
    border-radius: 8px
    padding: 6px 6px
    cursor: pointer
    color: inherit

    &:hover,
    &--open
        background: rgba(8, 135, 220, 0.08)

.login-teaser__factor-row
    display: flex
    align-items: center
    gap: 8px

.login-teaser__factor-label
    font-size: 12.5px
    line-height: 1.3

.login-teaser__factor-tip
    display: block
    margin: 6px 0 2px 28px
    font-size: 12px
    line-height: 1.4
    opacity: 0.82

.login-teaser__lucy
    display: flex
    gap: 10px
    align-items: flex-start
    margin: 14px 0 12px
    padding: 10px
    border-radius: 12px
    background: rgba(205, 126, 194, 0.12)
    border: 1px solid rgba(205, 126, 194, 0.35)

    strong
        font-size: 12px
    p
        margin: 2px 0 0
        font-size: 12px
        line-height: 1.4

.login-teaser__lucy-mark
    width: 22px
    height: 22px
    border-radius: 50%
    background: linear-gradient(135deg, #cd7ec2, #8e3d82)
    color: #fff
    font-size: 11px
    font-weight: 800
    display: inline-flex
    align-items: center
    justify-content: center
    flex: 0 0 22px

.login-teaser__steps-label
    font-size: 11px
    font-weight: 700
    letter-spacing: 0.04em
    text-transform: uppercase
    opacity: 0.65
    margin: 4px 4px 8px

.login-teaser__step
    display: block
    width: 100%
    text-align: left
    border: 0
    background: rgba(8, 135, 220, 0.06)
    border-radius: 10px
    padding: 10px 10px
    margin-bottom: 8px
    cursor: pointer
    color: inherit

    &--open
        background: rgba(8, 135, 220, 0.12)

.login-teaser__step-head
    display: flex
    align-items: center
    font-size: 13px
    font-weight: 700

.login-teaser__step-body
    display: block
    margin-top: 6px
    font-size: 12px
    line-height: 1.4
    opacity: 0.8

.login-teaser__chips
    display: flex
    flex-wrap: wrap
    gap: 6px
    margin-top: 8px

    &--targets
        margin: 0 4px 12px

.login-teaser__chip
    font-size: 11px
    font-weight: 600
    padding: 3px 8px
    border-radius: 999px
    background: #eef4ff
    color: #1d4ed8

.theme--dark .login-teaser__chip
    background: #312e81
    color: #c7d2fe

.login-teaser__cta
    font-weight: 700 !important
</style>
